import React, {
  memo,
  useCallback,
  useEffect,
  useState
} from 'react';
import cn from 'classnames';

import { getSearch } from '@/api';
import useApiCall from '@/hooks/useApiCall';
import useDebounce from '@/hooks/useDebounce';
import { regexpRef } from '@/utils/tools';
import { HouseCard } from '@/types/common';

import Button from '@/components/UI/Button';
import TextField from '@/components/UI/InputText';
import ResultCard from '@/components/ResultCard';
import SROnly from '@/components/UI/SROnly';

interface SearchMobileProps {
  lang: string;
  placeholder: string;
}

const SearchMobile = ({
  lang,
  placeholder
}: SearchMobileProps) => {
  const [result, setResult] = useState<HouseCard | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 250);

  const onSearch = useCallback(() => {
    const options = {
      q: debouncedSearchTerm,
      lang
    };
    return getSearch(options);
  }, [debouncedSearchTerm, lang]);

  const onSuccess = useCallback((response: { data?: { result?: HouseCard } }) => {
    setResult(response.data?.result ?? null);
  }, []);

  const onChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setSearchTerm(value);

    if (!value.toLowerCase().match(regexpRef)) {
      setResult(null);
    }
  }, []);

  const onSubmit = useCallback((event: React.FormEvent) => {
    event.preventDefault();

    if (result) {
      window.location.assign(result.url);
    }
  }, [result]);

  const {
    processing,
    callback
  } = useApiCall(onSearch, onSuccess);

  useEffect(() => {
    if (!debouncedSearchTerm) {
      return;
    }

    callback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]);

  const hasError = !processing && !result
    && searchTerm === debouncedSearchTerm
    && debouncedSearchTerm.match(regexpRef);

  return (
    <form
      className="navigation-search"
      onSubmit={onSubmit}
    >
      <div className="form-field">
        <SROnly
          component="label"
          htmlFor="search_mobile"
        >
          {placeholder}
        </SROnly>

        <TextField
          className={cn('navigation-search-input', { error: hasError })}
          name="search_mobile"
          id="search_mobile"
          placeholder={placeholder}
          value={searchTerm}
          onChange={onChange}
        />
      </div>

      <Button
        className="navigation-search-button"
        type="submit"
      />

      {!!result && (
        <ResultCard house={result} />
      )}
    </form>
  );
};

export default memo(SearchMobile);
